import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionTitle from "@/components/base/SectionTitle";
import WorkGrid from "@/components/feature/WorkGrid";
import { works } from "@/mocks/works";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getRelatedWorks, typeLabel, typePath } from "@/utils/work";

const shareTargets = [
  { icon: "ri-weibo-fill", label: "微博" },
  { icon: "ri-wechat-fill", label: "微信" },
  { icon: "ri-link", label: "复制链接" },
];

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = useMemo(() => works.find((item) => item.id === id), [id]);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  usePageMeta({
    title: work
      ? `${work.title} - 奇米国产自拍影视详情介绍`
      : "影视详情 - 国产自拍",
    description: work
      ? `${work.title}（${work.year}，${work.genre}）导演 ${work.director}。${work.summary}`
      : undefined,
    keywords: work
      ? `${work.title},${work.genre},${work.tags.join(",")},国产自拍`
      : undefined,
  });

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!work) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-film-line text-3xl" />
        </span>
        <p className="mt-4 text-base text-foreground-700">
          影视作品不存在或已下架
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/"
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelatedWorks(work, works, 4);
  const favorited = isFavorite(work.id);
  const label = typeLabel(work.type);

  const infoRows = [
    { label: "类别", value: label },
    { label: "类型", value: work.genre },
    { label: "上映年份", value: `${work.year} 年` },
    { label: "地区", value: work.region },
    { label: "导演 / 制作", value: work.director },
    { label: "主演 / 嘉宾", value: work.cast.join("、") },
  ];

  return (
    <div className="mx-auto w-full px-4 py-6 md:px-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", path: "/" },
          { label, path: typePath(work.type) },
          { label: work.title },
        ]}
      />

      <div className="mt-4 grid grid-cols-1 gap-6 rounded-lg border border-background-200 bg-background-100 p-4 md:grid-cols-[300px_1fr] md:gap-8 md:p-7">
        <div className="mx-auto w-full max-w-[300px] md:mx-0">
          <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={work.poster}
              alt={`${work.title} 高清海报`}
              title={`${work.title} - 国产自拍影视详情`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {label}
            </span>
            {work.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            {work.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="flex items-baseline gap-1">
              <span className="font-heading text-2xl font-black text-accent-600 md:text-3xl">
                {work.rating.toFixed(1)}
              </span>
              <span className="text-xs text-foreground-500">评分</span>
            </span>
            <span className="flex items-center gap-1 text-sm text-foreground-600">
              <i className="ri-fire-line text-accent-500" />
              热度 {work.popularity}
            </span>
            <span className="flex items-center gap-1 text-sm text-foreground-600">
              <i className="ri-calendar-line" />
              {work.year}
            </span>
          </div>

          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {work.summary}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
            {infoRows.map((row) => (
              <div key={row.label} className="flex gap-2 text-sm">
                <span className="w-20 shrink-0 text-foreground-500">{row.label}</span>
                <span className="text-foreground-900">{row.value}</span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                const nowFav = toggleFavorite(work.id);
                showToast(nowFav ? "已收藏，可在「我的收藏」查看" : "已取消收藏");
              }}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                favorited
                  ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-fill text-lg" : "ri-heart-line text-lg"} />
              {favorited ? "已收藏" : "收藏"}
            </button>

            <Link
              to={typePath(work.type)}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-folder-line text-lg" />
              更多{label}
            </Link>

            <div className="flex items-center gap-2">
              {shareTargets.map((target) => (
                <button
                  key={target.label}
                  type="button"
                  title={`分享到${target.label}`}
                  aria-label={`分享到${target.label}`}
                  onClick={() => showToast(`分享到${target.label}（前端演示）`)}
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
                >
                  <i className={`${target.icon} text-lg`} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
        <SectionTitle title="剧情简介" />
        <p className="whitespace-pre-line text-sm leading-loose text-foreground-700 md:text-base">
          {work.synopsis}
        </p>
      </section>

      <section className="mt-10">
        <SectionTitle title="相关推荐" subtitle={`与《${work.title}》相似的${label}作品`} />
        <WorkGrid works={related} columns={4} />
      </section>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 animate-fadeUp rounded-md bg-foreground-950/90 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}